import { type ComponentType } from "react";
import { createBrowserRouter, Navigate } from "react-router-dom";
import { AuthGuard } from "@/components/auth/AuthGuard";
import { RootLayout } from "@/components/layout/RootLayout";
import { LoginPage } from "@/components/auth/LoginPage";
import { CallbackPage } from "@/components/auth/CallbackPage";
import { SessionExpiredPage } from "@/components/auth/SessionExpiredPage";
import { AdminErrorBoundary } from "@/components/shared/AdminErrorBoundary";
import { NotFoundPage } from "@/pages/NotFoundPage";

/**
 * Wrap a page component with AdminErrorBoundary so errors are contained
 * per-section instead of crashing the entire app.
 */
function withBoundary(
  Comp: ComponentType,
  section: string,
): ComponentType {
  return function BoundedPage() {
    return (
      <AdminErrorBoundary section={section}>
        <Comp />
      </AdminErrorBoundary>
    );
  };
}

export const router = createBrowserRouter([
  {
    path: "/login",
    element: <LoginPage />,
  },
  {
    path: "/callback",
    element: <CallbackPage />,
  },
  {
    path: "/session-expired",
    element: <SessionExpiredPage />,
  },
  {
    path: "/",
    element: (
      <AuthGuard>
        <RootLayout />
      </AuthGuard>
    ),
    children: [
      { index: true, element: <Navigate to="/dashboard" replace /> },
      {
        path: "dashboard",
        lazy: () =>
          import("@/pages/dashboard/DashboardPage").then((m) => ({
            Component: withBoundary(m.DashboardPage, "dashboard"),
          })),
      },
      {
        path: "access-requests",
        lazy: () =>
          import("@/pages/access-requests/AccessRequestsPage").then((m) => ({
            Component: withBoundary(m.AccessRequestsPage, "access-requests"),
          })),
      },
      {
        path: "tenants",
        lazy: () =>
          import("@/pages/tenants/TenantsPage").then((m) => ({
            Component: withBoundary(m.TenantsPage, "tenants"),
          })),
      },
      {
        path: "tenants/:id",
        lazy: () =>
          import("@/pages/tenants/TenantDetailPage").then((m) => ({
            Component: withBoundary(m.TenantDetailPage, "tenants"),
          })),
      },
      {
        path: "users",
        lazy: () =>
          import("@/pages/users/UsersPage").then((m) => ({
            Component: withBoundary(m.UsersPage, "users"),
          })),
      },
      {
        path: "contacts",
        lazy: () =>
          import("@/pages/contacts/ContactsPage").then((m) => ({
            Component: withBoundary(m.ContactsPage, "contacts"),
          })),
      },
      {
        path: "conversations",
        lazy: () =>
          import("@/pages/conversations/ConversationsPage").then((m) => ({
            Component: withBoundary(m.ConversationsPage, "conversations"),
          })),
      },
      {
        path: "conversations/:id",
        lazy: () =>
          import("@/pages/conversations/ConversationDetailPage").then((m) => ({
            Component: withBoundary(m.ConversationDetailPage, "conversations"),
          })),
      },
      {
        path: "models",
        lazy: () =>
          import("@/pages/models/ModelsPage").then((m) => ({
            Component: withBoundary(m.ModelsPage, "models"),
          })),
      },
      {
        path: "tools",
        lazy: () =>
          import("@/pages/tools/ToolsPage").then((m) => ({
            Component: withBoundary(m.ToolsPage, "tools"),
          })),
      },
      {
        path: "personas",
        lazy: () =>
          import("@/pages/personas/PersonasPage").then((m) => ({
            Component: withBoundary(m.PersonasPage, "personas"),
          })),
      },
      {
        path: "settings",
        lazy: () =>
          import("@/pages/settings/SettingsPage").then((m) => ({
            Component: withBoundary(m.SettingsPage, "settings"),
          })),
      },
      {
        path: "feature-flags",
        lazy: () =>
          import("@/pages/feature-flags/FeatureFlagsPage").then((m) => ({
            Component: withBoundary(m.FeatureFlagsPage, "feature-flags"),
          })),
      },
      {
        path: "audit",
        lazy: () =>
          import("@/pages/audit/AuditPage").then((m) => ({
            Component: withBoundary(m.AuditPage, "audit"),
          })),
      },
      {
        path: "health",
        lazy: () =>
          import("@/pages/health/ApiHealthPage").then((m) => ({
            Component: withBoundary(m.ApiHealthPage, "health"),
          })),
      },
      {
        path: "insights/onboarding",
        lazy: () =>
          import("@/pages/insights/OnboardingInsightsPage").then((m) => ({
            Component: withBoundary(m.OnboardingInsightsPage, "insights"),
          })),
      },
      {
        path: "insights/economics",
        lazy: () =>
          import("@/pages/insights/EconomicsInsightsPage").then((m) => ({
            Component: withBoundary(m.EconomicsInsightsPage, "insights"),
          })),
      },
      {
        path: "insights/performance",
        lazy: () =>
          import("@/pages/insights/PerformanceInsightsPage").then((m) => ({
            Component: withBoundary(m.PerformanceInsightsPage, "insights"),
          })),
      },
      {
        path: "insights/models",
        lazy: () =>
          import("@/pages/insights/ModelInsightsPage").then((m) => ({
            Component: withBoundary(m.ModelInsightsPage, "insights"),
          })),
      },
      {
        path: "insights/activity",
        lazy: () =>
          import("@/pages/insights/ActivityInsightsPage").then((m) => ({
            Component: withBoundary(m.ActivityInsightsPage, "insights"),
          })),
      },
      {
        path: "insights/engagement",
        lazy: () =>
          import("@/pages/insights/EngagementInsightsPage").then((m) => ({
            Component: withBoundary(m.EngagementInsightsPage, "insights"),
          })),
      },
      {
        path: "insights/quality",
        lazy: () =>
          import("@/pages/insights/QualityInsightsPage").then((m) => ({
            Component: withBoundary(m.QualityInsightsPage, "insights"),
          })),
      },
      {
        path: "insights/conversations",
        lazy: () =>
          import("@/pages/insights/ConversationsInsightsPage").then((m) => ({
            Component: withBoundary(m.ConversationsInsightsPage, "insights"),
          })),
      },
      {
        // Sprint 26 Fase 4
        path: "insights/quality-alerts",
        lazy: () =>
          import("@/pages/insights/QualityAlertsPage").then((m) => ({
            Component: withBoundary(m.QualityAlertsPage, "insights"),
          })),
      },
      {
        // Sprint 26 Fase 4
        path: "insights/repair-telemetry",
        lazy: () =>
          import("@/pages/insights/RepairTelemetryViewer").then((m) => ({
            Component: withBoundary(m.RepairTelemetryViewer, "insights"),
          })),
      },
      {
        path: "assessment",
        lazy: () =>
          import("@/pages/assessment/AssessmentPage").then((m) => ({
            Component: m.AssessmentPage,
          })),
      },
      {
        path: "trace",
        lazy: () =>
          import("@/pages/trace/TraceViewerPage").then((m) => ({
            Component: m.TraceViewerPage,
          })),
      },
      {
        path: "bench",
        lazy: () =>
          import("@/pages/bench/BenchPage").then((m) => ({
            Component: withBoundary(m.BenchPage, "bench"),
          })),
      },
      {
        path: "bench/runs/:id",
        lazy: () =>
          import("@/pages/bench/RunDetailPage").then((m) => ({
            Component: withBoundary(m.RunDetailPage, "bench"),
          })),
      },
      {
        path: "email-log",
        lazy: () =>
          import("@/pages/email-log/EmailLogPage").then((m) => ({
            Component: withBoundary(m.EmailLogPage, "email-log"),
          })),
      },
      {
        path: "consent",
        lazy: () =>
          import("@/pages/consent/ConsentAuditPage").then((m) => ({
            Component: withBoundary(m.ConsentAuditPage, "consent"),
          })),
      },
      {
        path: "library",
        lazy: () =>
          import("@/pages/library/LibraryPage").then((m) => ({
            Component: withBoundary(m.LibraryPage, "library"),
          })),
      },
      { path: "*", element: <NotFoundPage /> },
    ],
  },
]);
